import React, { useState, useEffect, useCallback } from 'react';
import { Table, DatePicker, Space, Button, message } from 'antd';
import { FileExcelOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import Rest from 'tools/rest';
import { api } from 'tools/rest';
import Preloader from 'components/preloader';

const breakdownTh = {
  textAlign: 'left',
  fontWeight: 600,
  fontSize: 12,
  color: '#595959',
  padding: '6px 10px 6px 0',
  borderBottom: '1px solid #e8e8e8',
  whiteSpace: 'nowrap',
};

const breakdownTd = {
  padding: '8px 10px 8px 0',
  verticalAlign: 'top',
  borderBottom: '1px solid #f5f5f5',
  lineHeight: 1.45,
};

const BreakdownCell = ({ breakdown, unit }) => {
  const lines = breakdown || [];
  if (lines.length === 0) return '—';
  return (
    <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 13,
          color: '#262626',
        }}
      >
        <thead>
          <tr>
            <th style={{ ...breakdownTh, width: '68%' }}>Адрес</th>
            <th style={{ ...breakdownTh, width: '32%', textAlign: 'right' }}>Кол-во</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((b, i) => (
            <tr key={i}>
              <td style={breakdownTd}>{b.address}</td>
              <td style={{ ...breakdownTd, textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 500 }}>
                {unit ? `${b.quantity} ${unit}` : b.quantity}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const MonthlyReportTab = ({ dataVersion = 0, activeTabKey = 'storage' }) => {
  const [month, setMonth] = useState(() => dayjs());
  const [loading, setLoading] = useState(false);
  const [xlsLoading, setXlsLoading] = useState(false);
  const [data, setData] = useState(null);

  const visible = activeTabKey === 'monthly_report';

  const loadReport = useCallback(() => {
    setLoading(true);
    Rest.get('/api/v1/material_moves/monthly_report.json', {
      params: {
        year: month.year(),
        month: month.month() + 1,
      },
    })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [month]);

  useEffect(() => {
    if (!visible) return;
    loadReport();
  }, [visible, month, dataVersion, loadReport]);

  const downloadExcel = useCallback(async () => {
    setXlsLoading(true);
    try {
      const response = await api.get('/api/v1/material_moves/monthly_report_xls', {
        params: {
          year: month.year(),
          month: month.month() + 1,
        },
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/vnd.ms-excel' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `materials_report_${month.format('YYYY-MM')}.xls`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      message.error('Не удалось сформировать файл');
    } finally {
      setXlsLoading(false);
    }
  }, [month]);

  const columns = [
    {
      title: 'Материал',
      dataIndex: 'material_name',
      key: 'material_name',
      width: '22%',
      ellipsis: true,
    },
    {
      title: 'Расход по адресам и задачам',
      dataIndex: 'breakdown',
      key: 'breakdown',
      render: (_, row) => <BreakdownCell breakdown={row.breakdown} unit={row.unit} />,
    },
    {
      title: 'Сумма',
      dataIndex: 'total_quantity',
      key: 'total_quantity',
      width: 130,
      align: 'right',
      render: (q, row) => {
        const u = row.unit;
        return u ? `${q} ${u}` : String(q);
      },
    },
  ];

  return (
    <Preloader loading={loading && !data}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space wrap align="center">
          <DatePicker
            picker="month"
            value={month}
            onChange={(d) => d && setMonth(d)}
            format="MM.YYYY"
            allowClear={false}
          />
          <Button
            type="primary"
            icon={<FileExcelOutlined />}
            loading={xlsLoading}
            onClick={downloadExcel}
          >
            Скачать Excel
          </Button>
        </Space>
        <Table
          rowKey="material_id"
          columns={columns}
          dataSource={data?.rows || []}
          pagination={false}
          size="small"
          locale={{ emptyText: 'Нет данных за выбранный месяц' }}
        />
      </Space>
    </Preloader>
  );
};

export default MonthlyReportTab;
